/**
 * Resumable chunked uploads (web-plan.md F2, chat-api.md §2.6): sha256 over
 * the file BEFORE any byte moves (the dedup), then 1 MiB PATCHes whose
 * progress is the server's count and never ours, then `finish`.
 *
 * The three "the upload was destroyed" cases (sha mismatch, sniff refusal,
 * missing photo geometry) all answer 404 on a repeat `finish` — the caller
 * starts over from `POST /v1/uploads`; nothing here retries them.
 */
import type { FileKind } from "../../gen/dto";

import { session } from "../session";
import { assertCurrent, currentLifetime, type SessionLifetime } from "../lifetime";
import {
  CLIENT_HEADER,
  CLIENT_NAME,
  acceptLanguage,
  busyLeftMs,
  busySentence,
  describeTransport,
  errorWords,
  noteBusy,
  serverSentence,
} from "./rest";

export const CHUNK_SIZE = 1024 * 1024;
const BULK_TIMEOUT_MS = 60_000;
const CONTROL_TIMEOUT_MS = 10_000;
/** How many times a step waits out a gateway with too much in work before it
 * reports that (docs/admission.md D1075): five to eight seconds a wait by
 * default, so about a minute. A refused chunk would otherwise end an upload of a
 * hundred, which only picking the file again resumes. */
const BUSY_PATIENCE = 8;

export interface UploadResult {
  file_id: string;
  width?: number;
  height?: number;
  mime?: string;
  size: number;
}

export class UploadError extends Error {
  readonly status: number;
  constructor(status: number, message: string) {
    super(message);
    this.status = status;
  }
}

const KIND_CEILINGS: Record<string, number> = {
  // The *original* budget (D233), not the preview budget: a compliant client
  // uploads at most a 300 KiB preview under this kind, but avatars take the
  // same door and so do clients older than D233.
  photo: 5 * 1024 * 1024,
  voice: 8 * 1024 * 1024,
  video: 100 * 1024 * 1024,
  video_message: 100 * 1024 * 1024,
  file: 100 * 1024 * 1024,
};

export function kindCeiling(kind: FileKind): number {
  return KIND_CEILINGS[kind] ?? 0;
}

export interface BeginExtras {
  width?: number;
  height?: number;
  durationMs?: number;
  fileName?: string;
}

/** The whole three-step protocol. `progress` is 0..=1 and must not block.
 * `signal` stops it between requests and mid-request — the cross on the pill
 * over the composer (chat-plan.md D398); what was uploaded by then is left to
 * the gateway's expiry. */
export async function upload(
  blob: Blob,
  kind: FileKind,
  extras: BeginExtras,
  progress: (fraction: number) => void,
  opts: { unlimited?: boolean; signal?: AbortSignal } = {},
): Promise<UploadResult> {
  const lifetime = currentLifetime();
  const account = session();
  if (!account) throw new UploadError(0, "not logged in");
  const context: UploadContext = {
    lifetime,
    jwt: account.jwt,
    ...(opts.signal !== undefined ? { signal: opts.signal } : {}),
  };
  opts.signal?.throwIfAborted();
  if (blob.size === 0) throw new UploadError(0, "empty file");
  const ceiling = kindCeiling(kind);
  if (!opts.unlimited && blob.size > ceiling) {
    throw new UploadError(
      0,
      `${Math.floor(blob.size / (1024 * 1024))} MiB is over the ${Math.floor(
        ceiling / (1024 * 1024),
      )} MiB ceiling for ${kind}`,
    );
  }

  // sha256 first — what makes the dedup possible. crypto.subtle wants the
  // whole buffer; at a 100 MiB ceiling that is an accepted cost (the
  // desktop streams in 256 KiB reads because files there can be anything).
  const digest = await crypto.subtle.digest("SHA-256", await blob.arrayBuffer());
  assertCurrent(lifetime);
  const sha256 = [...new Uint8Array(digest)].map((b) => b.toString(16).padStart(2, "0")).join("");

  const beginBody: Record<string, unknown> = {
    size: blob.size,
    sha256,
    // The server sniffs the real type from the bytes at finish; the declared
    // value only has to be in the allow-list for the kind.
    mime: blob.type || "application/octet-stream",
    kind,
  };
  if (extras.fileName !== undefined) beginBody["file_name"] = extras.fileName;
  if (extras.width !== undefined) beginBody["width"] = extras.width;
  if (extras.height !== undefined) beginBody["height"] = extras.height;
  if (extras.durationMs !== undefined) beginBody["duration_ms"] = extras.durationMs;

  const begin = await patiently(context, () =>
    json<{ file_id?: string; upload_id?: string; received?: number }>(
      "POST",
      "/v1/uploads",
      beginBody,
      CONTROL_TIMEOUT_MS,
      context,
    ),
  );
  assertCurrent(lifetime);

  // Deduped: the bytes are already there, nothing to transfer.
  if (begin.file_id !== undefined) {
    progress(1);
    return {
      file_id: begin.file_id,
      size: blob.size,
      ...(extras.width !== undefined ? { width: extras.width } : {}),
      ...(extras.height !== undefined ? { height: extras.height } : {}),
    };
  }

  const uploadId = begin.upload_id!;
  let received = begin.received ?? 0;

  while (received < blob.size) {
    const chunk = blob.slice(received, received + CHUNK_SIZE);
    const offset = received;
    const answer = await patiently(context, () =>
      bytes<{ received: number }>(
        "PATCH",
        `/v1/uploads/${uploadId}?offset=${offset}`,
        chunk,
        context,
      ),
    );
    assertCurrent(lifetime);
    // Trust the server's count, not ours: a resend after a retry must not
    // double-advance, and a non-advancing answer must not spin forever.
    if (answer.received <= received) {
      throw new UploadError(0, `upload stalled at ${received} bytes`);
    }
    received = answer.received;
    progress(received / blob.size);
  }

  const fin = await patiently(context, () =>
    json<{ file_id: string; width?: number; height?: number; mime?: string; size?: number }>(
      "POST",
      `/v1/uploads/${uploadId}/finish`,
      {},
      CONTROL_TIMEOUT_MS,
      context,
    ),
  );
  assertCurrent(lifetime);
  progress(1);
  return {
    file_id: fin.file_id,
    size: fin.size ?? blob.size,
    ...(fin.width !== undefined
      ? { width: fin.width }
      : extras.width !== undefined
        ? { width: extras.width }
        : {}),
    ...(fin.height !== undefined
      ? { height: fin.height }
      : extras.height !== undefined
        ? { height: extras.height }
        : {}),
    ...(fin.mime !== undefined ? { mime: fin.mime } : {}),
  };
}

interface UploadContext {
  lifetime: SessionLifetime;
  jwt: string;
  /** The send's own stop (D398), beside the session's. */
  signal?: AbortSignal;
}

/** What a request of this upload listens to: the session ending, its own
 * timeout, and the send being called off. */
function signalOf(context: UploadContext, timeoutMs: number): AbortSignal {
  const signals = [context.lifetime.signal, AbortSignal.timeout(timeoutMs)];
  if (context.signal !== undefined) signals.push(context.signal);
  return AbortSignal.any(signals);
}

/** `ask`, and again after each wait the gateway names (D1075), {@link BUSY_PATIENCE}
 * times at most. The send's stop and the session's end both cut a wait short. */
async function patiently<T>(context: UploadContext, ask: () => Promise<T>): Promise<T> {
  for (let waited = 0; ; waited += 1) {
    try {
      return await ask();
    } catch (e) {
      const left = busyLeftMs("transfers");
      if (!(e instanceof UploadError) || e.status !== 503 || left === 0 || waited >= BUSY_PATIENCE) {
        throw e;
      }
      await pause(left, context);
    }
  }
}

function pause(ms: number, context: UploadContext): Promise<void> {
  const signal = AbortSignal.any(
    context.signal === undefined
      ? [context.lifetime.signal]
      : [context.lifetime.signal, context.signal],
  );
  return new Promise((resolve, reject) => {
    if (signal.aborted) {
      reject(signal.reason);
      return;
    }
    const stop = () => {
      clearTimeout(timer);
      reject(signal.reason);
    };
    const timer = setTimeout(() => {
      signal.removeEventListener("abort", stop);
      resolve();
    }, ms);
    signal.addEventListener("abort", stop, { once: true });
  });
}

/** The gateway's wait still running (D1075): refused here, in its own words. */
function heldBack(path: string): void {
  const sentence = busySentence(path);
  if (sentence !== null) throw new UploadError(503, sentence);
}

/** A send called off from the pill (D398) is not a transport failure: the
 * abort is rethrown as itself, for the caller to recognise, rather than worded
 * as a network error. */
function stopped(context: UploadContext): void {
  context.signal?.throwIfAborted();
}

async function json<T>(
  method: string,
  path: string,
  body: unknown,
  timeoutMs: number,
  context: UploadContext,
): Promise<T> {
  assertCurrent(context.lifetime);
  stopped(context);
  heldBack(path);
  let response: Response;
  try {
    response = await fetch(path, {
      method,
      headers: {
        "Content-Type": "application/json",
        "Accept-Language": acceptLanguage(),
        [CLIENT_HEADER]: CLIENT_NAME,
        Authorization: `Bearer ${context.jwt}`,
      },
      body: JSON.stringify(body),
      signal: signalOf(context, timeoutMs),
    });
  } catch (e) {
    stopped(context);
    throw new UploadError(0, describeTransport(e));
  }
  assertCurrent(context.lifetime);
  const result = await answer<T>(path, response);
  assertCurrent(context.lifetime);
  return result;
}

async function bytes<T>(method: string, path: string, chunk: Blob, context: UploadContext): Promise<T> {
  assertCurrent(context.lifetime);
  stopped(context);
  heldBack(path);
  let response: Response;
  try {
    response = await fetch(path, {
      method,
      headers: {
        "Accept-Language": acceptLanguage(),
        [CLIENT_HEADER]: CLIENT_NAME,
        Authorization: `Bearer ${context.jwt}`,
      },
      body: chunk,
      signal: signalOf(context, BULK_TIMEOUT_MS),
    });
  } catch (e) {
    stopped(context);
    throw new UploadError(0, describeTransport(e));
  }
  assertCurrent(context.lifetime);
  const result = await answer<T>(path, response);
  assertCurrent(context.lifetime);
  return result;
}

async function answer<T>(path: string, response: Response): Promise<T> {
  const text = await response.text().catch(() => "");
  if (response.status < 200 || response.status >= 300) {
    let message = errorWords().errorHttp(response.status);
    try {
      message = serverSentence(JSON.parse(text) as { error?: string; message?: string }) ?? message;
    } catch {
      // The status is the message.
    }
    noteBusy(path, response, message);
    throw new UploadError(response.status, message);
  }
  try {
    return JSON.parse(text) as T;
  } catch {
    throw new UploadError(0, errorWords().errorBadAnswer());
  }
}
